/**
 * <ttt-banner> - shared slideshow controller for the TTT banner sections
 * (ttt-split-banner and ttt-hero-banner).
 *
 * Markup contract:
 *   <ttt-banner data-autoplay="true" data-speed="6" data-loop="true" data-transition="slide">
 *     <div data-ttt-track>
 *       <div class="ttt-banner__slide" data-ttt-slide>...</div>
 *     </div>
 *     <button data-ttt-prev>  <button data-ttt-next>
 *     <button data-ttt-pause>
 *     <button data-ttt-goto="0">   (dots / tabs / numbers)
 *   </ttt-banner>
 *
 * Every control is optional - the section templates render only what the
 * merchant has switched on, and this element wires up whatever it finds.
 */
if (!customElements.get('ttt-banner')) {
  class TttBanner extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.track = this.querySelector('[data-ttt-track]');
      this.slides = Array.from(this.querySelectorAll('[data-ttt-slide]'));
      if (this.slides.length === 0) return;

      this.prevBtns = Array.from(this.querySelectorAll('[data-ttt-prev]'));
      this.nextBtns = Array.from(this.querySelectorAll('[data-ttt-next]'));
      this.pauseBtns = Array.from(this.querySelectorAll('[data-ttt-pause]'));
      this.gotoBtns = Array.from(this.querySelectorAll('[data-ttt-goto]'));
      this.liveRegion = this.querySelector('[data-ttt-live]');

      this.index = 0;
      this.loop = this.dataset.loop !== 'false';
      this.transition = this.dataset.transition || 'slide';
      this.rtl = document.dir === 'rtl';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reduceMotion;
      this.speed = (parseFloat(this.dataset.speed) || 6) * 1000;
      this.paused = false;
      this.timer = null;

      this.prevBtns.forEach((btn) => btn.addEventListener('click', () => this.step(-1, true)));
      this.nextBtns.forEach((btn) => btn.addEventListener('click', () => this.step(1, true)));
      this.pauseBtns.forEach((btn) => btn.addEventListener('click', () => this.togglePause()));

      this.gotoBtns.forEach((btn) => {
        btn.addEventListener('click', () => this.select(parseInt(btn.dataset.tttGoto, 10), true));
      });

      this.addEventListener('keydown', this.onKeyDown.bind(this));

      if (this.slides.length > 1) {
        this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
      }

      if (this.autoplay) {
        this.addEventListener('mouseenter', () => this.suspend());
        this.addEventListener('mouseleave', () => this.resume());
        this.addEventListener('focusin', () => this.suspend());
        this.addEventListener('focusout', () => this.resume());
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.suspend();
          else this.resume();
        });
        this.observe();
      }

      this.render(false);

      // Theme editor: jump to the block the merchant selected.
      this.addEventListener('shopify:block:select', (evt) => {
        const i = this.slides.indexOf(evt.target.closest('[data-ttt-slide]'));
        if (i > -1) this.select(i, true);
        this.suspend();
      });
      this.addEventListener('shopify:block:deselect', () => this.resume());
    }

    disconnectedCallback() {
      this.stop();
      if (this.observer) this.observer.disconnect();
    }

    /* ----------------------------------------------------------- navigation */

    step(direction, userInitiated) {
      let next = this.index + direction;
      if (next < 0) next = this.loop ? this.slides.length - 1 : 0;
      if (next > this.slides.length - 1) next = this.loop ? 0 : this.slides.length - 1;
      this.select(next, userInitiated);
    }

    select(index, userInitiated) {
      if (Number.isNaN(index) || index === this.index) return;
      this.index = index;
      this.render(true);
      if (userInitiated && this.autoplay && !this.paused) this.start();
    }

    render(announce) {
      const total = this.slides.length;

      this.slides.forEach((slide, i) => {
        const current = i === this.index;
        slide.classList.toggle('is-active', current);
        slide.setAttribute('aria-hidden', current ? 'false' : 'true');
        // Keep hidden slides out of the tab order without hiding them from
        // the layout (the track is sized by the tallest slide).
        slide.querySelectorAll('a, button, input, select, textarea').forEach((el) => {
          if (current) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });

      if (this.transition === 'slide' && this.track) {
        const offset = this.rtl ? this.index * 100 : this.index * -100;
        this.track.style.transform = `translate3d(${offset}%, 0, 0)`;
      }

      this.gotoBtns.forEach((btn) => {
        const active = parseInt(btn.dataset.tttGoto, 10) === this.index;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (!this.loop) {
        this.prevBtns.forEach((btn) => { btn.disabled = this.index === 0; });
        this.nextBtns.forEach((btn) => { btn.disabled = this.index === total - 1; });
      }

      this.querySelectorAll('[data-ttt-counter]').forEach((el) => {
        el.textContent = this.index + 1;
      });

      if (announce && this.liveRegion) {
        this.liveRegion.textContent = `Slide ${this.index + 1} of ${total}`;
      }
    }

    /* ------------------------------------------------------------- autoplay */

    observe() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.resume();
          else this.suspend();
        });
      });
      this.observer.observe(this);
    }

    start() {
      this.stop();
      this.timer = setInterval(() => this.step(1, false), this.speed);
      this.setAttribute('data-playing', 'true');
      this.pauseBtns.forEach((btn) => {
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', btn.dataset.labelPause || 'Pause slideshow');
      });
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
      this.setAttribute('data-playing', 'false');
      this.pauseBtns.forEach((btn) => {
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', btn.dataset.labelPlay || 'Play slideshow');
      });
    }

    /** Temporary halt (hover, off-screen, editor) - respects the manual pause. */
    suspend() {
      if (this.autoplay) this.stop();
    }

    resume() {
      if (this.autoplay && !this.paused && !document.hidden) this.start();
    }

    /** Manual pause via the play/pause control - sticky until clicked again. */
    togglePause() {
      this.paused = !this.paused;
      if (this.paused) this.stop();
      else this.start();
    }

    /* --------------------------------------------------------------- events */

    onKeyDown(evt) {
      if (this.slides.length < 2) return;
      if (evt.key === 'ArrowLeft') {
        evt.preventDefault();
        this.step(this.rtl ? 1 : -1, true);
      } else if (evt.key === 'ArrowRight') {
        evt.preventDefault();
        this.step(this.rtl ? -1 : 1, true);
      }
    }

    onTouchStart(evt) {
      this.touchStartX = evt.changedTouches[0].screenX;
      this.touchStartY = evt.changedTouches[0].screenY;
    }

    onTouchEnd(evt) {
      if (this.touchStartX === undefined) return;
      const deltaX = evt.changedTouches[0].screenX - this.touchStartX;
      const deltaY = evt.changedTouches[0].screenY - this.touchStartY;
      // Ignore mostly-vertical gestures so page scrolling still works.
      if (Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY)) {
        this.step(deltaX < 0 ? 1 : -1, true);
      }
      this.touchStartX = undefined;
    }
  }

  customElements.define('ttt-banner', TttBanner);
}
